import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  NOTEBOOK_PAGE_GAP,
  NOTEBOOK_PAGE_HEIGHT,
  NOTEBOOK_PAGE_WIDTH,
  NOTEBOOK_WORKSPACE_BG,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  usersIcon,
  share,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";

import "./index.scss";

import { CanvasLibrarySidebar } from "./components/CanvasLibrarySidebar";
import { CanvasTitleBar } from "./components/CanvasTitleBar";
import { SharePublicViewer } from "./components/SharePublicViewer";
import { StickyNoteToolHandler } from "./components/StickyNoteButton";
import { AuthGate } from "./auth/AuthGate";
import { UserManagementDialog } from "./auth/UserManagementDialog";
import { GridSettingsDialog } from "./components/GridSettingsDialog";
import { PresentationExitDialog } from "./components/PresentationExitDialog";
import { exportSceneToPdf } from "./data/exportPdf";
import { libraryApi, type NotebookPageStyle } from "./data/libraryApi";
import {
  cacheScene,
  clearPending,
  getCachedScene,
  getPendingIds,
  getPendingScene,
  setPending,
} from "./data/offlineStore";
import * as libraryStore from "./data/libraryStore";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const excalidrawAPI = useExcalidrawAPI();

  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // canvas library state
  // ---------------------------------------------------------------------------
  const ACTIVE_CANVAS_STORAGE_KEY = "excalidraw-active-canvas-id";
  const [activeCanvasId, setActiveCanvasId] = useState<string | null>(() =>
    typeof window !== "undefined"
      ? window.localStorage.getItem(ACTIVE_CANVAS_STORAGE_KEY)
      : null,
  );
  const saveTimerRef = useRef<number | null>(null);
  // True once the active canvas has been fully loaded (server / pending / cache).
  // Guards onChange from writing a spurious pending before the initial load
  // completes — prevents an empty Excalidraw init-onChange from racing the
  // canvas-load effect and overwriting a legitimate server scene.
  const canvasReadyRef = useRef(false);
  // Stable refs so the async sync callback always sees the latest values
  // without needing them as effect dependencies (which would re-register all
  // event listeners on every canvas switch).
  const activeCanvasIdRef = useRef<string | null>(activeCanvasId);
  const excalidrawAPIRef = useRef(excalidrawAPI);
  // Server-side updated_at of the canvas version we last applied.
  // Used to detect when a remote session has saved a newer version.
  const lastCanvasUpdatedAtRef = useRef<number>(0);
  const [libraryVersion, setLibraryVersion] = useState(0);
  const bumpLibrary = useCallback(() => {
    setLibraryVersion((v) => v + 1);
  }, []);

  // Keep stable refs in sync with their state/prop counterparts.
  useEffect(() => {
    activeCanvasIdRef.current = activeCanvasId;
    lastCanvasUpdatedAtRef.current = 0; // reset on canvas switch so next sync re-checks
  }, [activeCanvasId]);
  useEffect(() => {
    excalidrawAPIRef.current = excalidrawAPI;
  }, [excalidrawAPI]);

  // PizzarWeb: presentation mode is EPHEMERAL — annotations are never saved to
  // the board. We hide our own chrome, snapshot the scene on entry, suppress
  // backend autosave while active, and on exit restore the snapshot (after an
  // optional "save as PDF / new board" modal).
  const [isPresentation, setIsPresentation] = useState(false);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  // PizzarWeb: notebook (A4 paged) surface — drives the floating page bar
  // (◀ Hoja N/M ▶ · delete · add). `active` is the page most centered in view.
  const NOTEBOOK_STRIDE = NOTEBOOK_PAGE_HEIGHT + NOTEBOOK_PAGE_GAP;
  const [notebookUI, setNotebookUI] = useState<{
    mode: boolean;
    count: number;
    active: number;
    style: NotebookPageStyle;
    opacity: number;
  }>({ mode: false, count: 1, active: 0, style: "ruled", opacity: 100 });
  const [notebookStyleOpen, setNotebookStyleOpen] = useState(false);

  // Change the page-pattern opacity (0–100) of the open notebook. Applies live
  // and persists in the scene appState (onChange). Not echoed to the DB (it's a
  // per-scene visual setting that travels in the .excalidraw file).
  const setNotebookOpacity = useCallback((opacity: number) => {
    const api = excalidrawAPIRef.current;
    if (!api) {
      return;
    }
    const v = Math.max(0, Math.min(100, Math.round(opacity)));
    api.updateScene({
      appState: { notebookPageOpacity: v } as any,
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    setNotebookUI((s) => ({ ...s, opacity: v }));
  }, []);

  // Change the page background (grid/ruled/dotted/blank) of the open notebook:
  // applies live (updateScene), persists in the scene appState (onChange) AND
  // updates the canvas metadata so it survives across devices.
  const setNotebookStyle = useCallback((style: NotebookPageStyle) => {
    const api = excalidrawAPIRef.current;
    if (!api) {
      return;
    }
    api.updateScene({
      appState: { notebookPageStyle: style } as any,
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    setNotebookUI((s) => ({ ...s, style }));
    setNotebookStyleOpen(false);
    const id = activeCanvasIdRef.current;
    if (id) {
      void libraryStore.updateCanvas(id, { page_style: style });
    }
  }, []);

  const notebookActivePage = useCallback(
    (st: {
      height: number;
      scrollY: number;
      zoom: { value: number };
      notebookPageCount?: number;
    }) => {
      const z = st.zoom?.value || 1;
      const count = Math.max(1, Math.floor(st.notebookPageCount ?? 1));
      const worldCenterY = st.height / 2 / z - st.scrollY;
      return Math.max(
        0,
        Math.min(count - 1, Math.floor(worldCenterY / NOTEBOOK_STRIDE)),
      );
    },
    [NOTEBOOK_STRIDE],
  );

  const notebookGoToPage = useCallback(
    (i: number) => {
      const api = excalidrawAPIRef.current;
      if (!api) {
        return;
      }
      const st = api.getAppState() as AppState;
      const z = st.zoom.value || 1;
      const target = Math.max(
        0,
        Math.min(Math.max(1, Math.floor(st.notebookPageCount ?? 1)) - 1, i),
      );
      api.updateScene({
        appState: { scrollY: 40 / z - target * NOTEBOOK_STRIDE } as any,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
      setNotebookUI((s) => ({ ...s, active: target }));
    },
    [NOTEBOOK_STRIDE],
  );

  const addNotebookPage = useCallback(() => {
    const api = excalidrawAPIRef.current;
    if (!api) {
      return;
    }
    const st = api.getAppState() as AppState;
    if (!st.notebookMode) {
      return;
    }
    const next = Math.max(1, Math.floor(st.notebookPageCount ?? 1)) + 1;
    const z = st.zoom.value || 1;
    api.updateScene({
      appState: {
        notebookPageCount: next,
        // bring the freshly-added (last) page into view
        scrollY: 40 / z - (next - 1) * NOTEBOOK_STRIDE,
      } as any,
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    setNotebookUI((s) => ({ ...s, mode: true, count: next, active: next - 1 }));
  }, [NOTEBOOK_STRIDE]);

  const deleteNotebookPage = useCallback(() => {
    const api = excalidrawAPIRef.current;
    if (!api) {
      return;
    }
    const st = api.getAppState() as AppState;
    if (!st.notebookMode) {
      return;
    }
    const count = Math.max(1, Math.floor(st.notebookPageCount ?? 1));
    if (count <= 1) {
      return; // keep at least one page
    }
    const target = Math.min(notebookUI.active, count - 1);
    // Delete the elements that live on the target page and shift every element
    // on later pages up by one page (so the following pages move up).
    const els = api.getSceneElements();
    const nextElements = els.map((el) => {
      const cy = el.y + (el.height || 0) / 2;
      const page = Math.max(0, Math.floor(cy / NOTEBOOK_STRIDE));
      if (page === target) {
        return newElementWith(el, { isDeleted: true });
      }
      if (page > target) {
        return newElementWith(el, { y: el.y - NOTEBOOK_STRIDE });
      }
      return el;
    });
    const newCount = count - 1;
    const newActive = Math.min(target, newCount - 1);
    const z = st.zoom.value || 1;
    api.updateScene({
      elements: nextElements as any,
      appState: {
        notebookPageCount: newCount,
        scrollY: 40 / z - newActive * NOTEBOOK_STRIDE,
      } as any,
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
    setNotebookUI((s) => ({
      ...s,
      mode: true,
      count: newCount,
      active: newActive,
    }));
  }, [NOTEBOOK_STRIDE, notebookUI.active]);
  const presentationSnapshotRef = useRef<
    readonly OrderedExcalidrawElement[] | null
  >(null);
  const prevPresentationRef = useRef(false);

  const sceneSignature = (
    els: readonly { id: string; version: number; isDeleted?: boolean }[],
  ) =>
    els
      .filter((e) => !e.isDeleted)
      .map((e) => `${e.id}:${e.version}`)
      .join(",");

  const finishExitPresentation = useCallback(() => {
    const snapshot = presentationSnapshotRef.current;
    excalidrawAPI?.updateScene({
      ...(snapshot ? { elements: snapshot as any } : {}),
      appState: { presentationModeEnabled: false },
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    presentationSnapshotRef.current = null;
    setExitDialogOpen(false);
    setIsPresentation(false);
  }, [excalidrawAPI]);

  const requestExitPresentation = useCallback(() => {
    if (!excalidrawAPI) {
      return;
    }
    const snapshot = presentationSnapshotRef.current;
    const current = excalidrawAPI.getSceneElementsIncludingDeleted();
    const changed =
      !snapshot || sceneSignature(snapshot) !== sceneSignature(current);
    if (changed) {
      setExitDialogOpen(true);
    } else {
      finishExitPresentation();
    }
  }, [excalidrawAPI, finishExitPresentation]);

  const handleExitSavePdf = useCallback(async () => {
    if (!excalidrawAPI) {
      return;
    }
    try {
      await exportSceneToPdf(
        excalidrawAPI.getSceneElements(),
        excalidrawAPI.getAppState(),
        excalidrawAPI.getFiles(),
        excalidrawAPI.getName() || "presentacion",
      );
    } catch {
      /* ignore — still exit */
    }
    finishExitPresentation();
  }, [excalidrawAPI, finishExitPresentation]);

  const handleExitSaveAsNewBoard = useCallback(async () => {
    if (!excalidrawAPI) {
      return;
    }
    try {
      let base = "Presentación";
      if (activeCanvasId) {
        try {
          base = (await libraryApi.getCanvas(activeCanvasId)).name;
        } catch {
          /* keep fallback */
        }
      }
      const appState = excalidrawAPI.getAppState();
      await libraryApi.createCanvas({
        name: `${base} (presentación)`,
        scene: {
          type: "excalidraw",
          version: 2,
          source: "presentation",
          elements: excalidrawAPI.getSceneElements(),
          appState: { viewBackgroundColor: appState.viewBackgroundColor },
          files: excalidrawAPI.getFiles(),
        },
      });
      bumpLibrary();
      excalidrawAPI.setToast({ message: t("presentation.savedAsNewBoard") });
    } catch {
      /* ignore — still exit */
    }
    finishExitPresentation();
  }, [excalidrawAPI, activeCanvasId, bumpLibrary, finishExitPresentation]);

  // Alt+P exits presentation (the core action only enters), routed through the
  // modal so changes aren't silently lost.
  useEffect(() => {
    if (!isPresentation) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && e.code === "KeyP") {
        e.preventDefault();
        requestExitPresentation();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPresentation, requestExitPresentation]);

  useEffect(() => {
    if (!activeCanvasId || !excalidrawAPI) {
      return;
    }
    canvasReadyRef.current = false;
    let cancelled = false;
    // Build the appState to apply, layering notebook-mode surface settings on
    // top of the scene's own appState. The canvas metadata (canvas_type/
    // page_style) is the source of truth across devices; the scene's persisted
    // appState provides the page count. For a notebook we also frame the view on
    // the top of page 1 (fit page width). For a plain canvas, notebookMode is
    // forced off so reusing the same Excalidraw instance can't leak the surface.
    const buildAppState = (
      sceneAppState: unknown,
      meta: { canvas_type?: string; page_style?: string | null } | null,
    ): Record<string, unknown> => {
      const sa = (sceneAppState ?? {}) as Record<string, any>;
      const isNotebook =
        meta?.canvas_type === "notebook" || sa.notebookMode === true;
      if (!isNotebook) {
        return { ...sa, notebookMode: false };
      }
      const view = excalidrawAPI.getAppState();
      const margin = 64;
      const zoomValue = Math.min(
        1,
        Math.max(0.12, (view.width - margin * 2) / NOTEBOOK_PAGE_WIDTH),
      );
      return {
        ...sa,
        notebookMode: true,
        notebookPageStyle: sa.notebookPageStyle ?? meta?.page_style ?? "ruled",
        notebookPageCount: Math.max(1, Math.floor(sa.notebookPageCount ?? 1)),
        notebookPageOpacity: sa.notebookPageOpacity ?? 100,
        viewBackgroundColor: NOTEBOOK_WORKSPACE_BG,
        zoom: { value: zoomValue },
        scrollX: view.width / (2 * zoomValue) - NOTEBOOK_PAGE_WIDTH / 2,
        scrollY: 40 / zoomValue,
      };
    };
    const apply = (
      scene: {
        elements?: unknown;
        appState?: unknown;
        files?: Record<string, unknown>;
      },
      meta: { canvas_type?: string; page_style?: string | null } | null = null,
    ) => {
      if (cancelled) {
        return;
      }
      const filesArr = Object.values(scene.files || {}) as Parameters<
        typeof excalidrawAPI.addFiles
      >[0];
      if (filesArr.length > 0) {
        excalidrawAPI.addFiles(filesArr);
      }
      excalidrawAPI.updateScene({
        elements: (scene.elements ?? []) as any,
        appState: buildAppState(scene.appState, meta) as any,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
      // Mark canvas as ready so onChange can start writing pending entries.
      // Must be set AFTER updateScene so the first onChange triggered by it
      // already sees canvasReadyRef.current = true.
      canvasReadyRef.current = true;
    };
    (async () => {
      // Offline-first: unsynced local edits win; else backend (+cache); else
      // the local cache. Never drop the active canvas just because we're
      // offline — only if the backend says it's really gone.
      const pending = await getPendingScene(activeCanvasId);
      if (cancelled) {
        return;
      }
      if (pending) {
        apply(pending);
        return;
      }
      try {
        // Fetch scene + metadata in parallel so we can initialise
        // lastCanvasUpdatedAtRef (needed for remote-change detection in syncNow).
        const [scene, meta] = await Promise.all([
          libraryApi.getScene(activeCanvasId),
          libraryApi.getCanvas(activeCanvasId).catch(() => null),
        ]);
        if (cancelled) {
          return;
        }
        if (meta) {
          lastCanvasUpdatedAtRef.current = meta.updated_at;
        }
        void cacheScene(activeCanvasId, scene);
        apply(scene, meta);
      } catch {
        const cached = await getCachedScene(activeCanvasId);
        if (cancelled) {
          return;
        }
        if (cached) {
          apply(cached);
        } else if (navigator.onLine) {
          // online + no cache → the canvas was really deleted server-side
          window.localStorage.removeItem(ACTIVE_CANVAS_STORAGE_KEY);
          setActiveCanvasId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeCanvasId, excalidrawAPI]);

  // Flush the offline queue and refresh the library so the sidebar reflects
  // the server. The browser `online` event is unreliable (it can be missed
  // when a laptop/phone wakes or reconnects), and a single transient upload
  // failure would otherwise sit unsynced until the next reload. So we self-heal
  // by also syncing on tab focus / visibility and on a periodic interval.
  // syncNow() is a no-op when offline or already running, and cheap (local
  // IndexedDB reads only) when there is nothing pending.
  // Reconcile local pending scene with server version before uploading.
  // Uses Excalidraw's reconcileElements which merges element-by-element:
  // for each element ID the copy with the higher `version` wins; elements
  // present only in one version are always kept. This makes simultaneous
  // edits on two devices conflict-free — neither session's work is lost.
  //
  // Returns the merged CanvasScene, or null if no conflict was detected
  // (server unchanged) or reconcile could not be performed.
  const reconcileSceneForSync: libraryStore.SceneReconciler = useCallback(
    async (id, localScene) => {
      if (!navigator.onLine) {
        return null;
      }
      try {
        const meta = await libraryApi.getCanvas(id);
        // For the active canvas we know the exact baseline (lastCanvasUpdatedAtRef).
        // For other canvases (old pending from a previous session) we always try
        // to reconcile since we have no baseline.
        const baseUpdatedAt =
          id === activeCanvasIdRef.current ? lastCanvasUpdatedAtRef.current : 0;
        if (meta.updated_at <= baseUpdatedAt) {
          return null; // server has same or older version — no conflict
        }
        const serverScene = await libraryApi.getScene(id);
        const merged = reconcileElements(
          localScene.elements as RemoteExcalidrawElement[],
          serverScene.elements as RemoteExcalidrawElement[],
          excalidrawAPIRef.current?.getAppState() ?? ({} as never),
        );
        const mergedScene = {
          ...(localScene as unknown as Record<string, unknown>),
          elements: merged as unknown[],
        } as typeof localScene;
        // Apply the merged scene to the canvas if this is the active one
        const api = excalidrawAPIRef.current;
        if (id === activeCanvasIdRef.current && api) {
          canvasReadyRef.current = false;
          api.updateScene({
            elements: merged as any,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          canvasReadyRef.current = true;
          void setPending(id, mergedScene as never);
          void cacheScene(id, mergedScene as never);
        }
        return mergedScene;
      } catch {
        return null; // reconcile failed — caller falls back to local version
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    const sync = async () => {
      if (!navigator.onLine) {
        return;
      }
      await libraryStore.syncNow(reconcileSceneForSync);
      bumpLibrary();

      // Remote-change detection: if the active canvas has been saved by another
      // session since we last applied it (and we have no unsaved local edits),
      // silently re-fetch and apply the newer version.
      const canvasId = activeCanvasIdRef.current;
      const api = excalidrawAPIRef.current;
      if (!canvasId || !api || !navigator.onLine) {
        return;
      }
      try {
        const pendingIds = await getPendingIds();
        if (pendingIds.includes(canvasId)) {
          return; // local unsaved edits — don't overwrite with remote
        }
        const meta = await libraryApi.getCanvas(canvasId);
        if (meta.updated_at <= lastCanvasUpdatedAtRef.current) {
          return; // no remote change
        }
        const scene = await libraryApi.getScene(canvasId);
        void cacheScene(canvasId, scene);
        const filesArr = Object.values(scene.files || {}) as Parameters<
          typeof api.addFiles
        >[0];
        if (filesArr.length > 0) {
          api.addFiles(filesArr);
        }
        // Briefly suppress onChange pending writes so this programmatic update
        // doesn't immediately write a new pending entry (the server is already
        // the authoritative source — no pending needed).
        canvasReadyRef.current = false;
        api.updateScene({
          elements: (scene.elements ?? []) as any,
          appState: scene.appState as any,
          captureUpdate: CaptureUpdateAction.NEVER,
        });
        canvasReadyRef.current = true;
        lastCanvasUpdatedAtRef.current = meta.updated_at;
      } catch {
        // ignore — next sync cycle will retry
      }
    };
    const onOnline = () => {
      void sync();
    };
    const onFocus = () => {
      void sync();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void sync();
      }
    };
    void sync(); // on mount
    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const interval = window.setInterval(() => {
      void sync();
    }, 10000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
    };
    // reconcileSceneForSync is intentionally omitted: this effect wires up the
    // mount/online/focus/interval sync triggers once; re-subscribing whenever
    // the reconcile callback identity changes is unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bumpLibrary]);

  const handleSelectCanvas = useCallback(
    async (id: string) => {
      if (id === activeCanvasId) {
        return;
      }
      if (activeCanvasId && excalidrawAPI) {
        if (saveTimerRef.current !== null) {
          window.clearTimeout(saveTimerRef.current);
          saveTimerRef.current = null;
        }
        const scene = {
          type: "excalidraw",
          version: 2,
          source: "self-hosted",
          elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
          appState: {
            viewBackgroundColor:
              excalidrawAPI.getAppState().viewBackgroundColor,
          },
          files: excalidrawAPI.getFiles(),
        };
        void cacheScene(activeCanvasId, scene as never);
        void setPending(activeCanvasId, scene as never);
        if (navigator.onLine) {
          try {
            await libraryApi.saveScene(activeCanvasId, scene);
            await clearPending(activeCanvasId);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error("Failed to flush save before switch", e);
          }
        }
      }
      window.localStorage.setItem(ACTIVE_CANVAS_STORAGE_KEY, id);
      setActiveCanvasId(id);
    },
    [activeCanvasId, excalidrawAPI],
  );

  const handleCanvasDeleted = useCallback(
    (deletedId: string) => {
      if (deletedId !== activeCanvasId) {
        return;
      }
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      window.localStorage.removeItem(ACTIVE_CANVAS_STORAGE_KEY);
      setActiveCanvasId(null);
      if (excalidrawAPI) {
        excalidrawAPI.updateScene({
          elements: [],
          captureUpdate: CaptureUpdateAction.NEVER,
        });
      }
    },
    [activeCanvasId, excalidrawAPI],
  );

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          if (fileIds.length) {
            // Direct Firebase call (not through FileManager), so track manually
            FileStatusStore.updateStatuses(
              fileIds.map((id) => [id, "loading"]),
            );
          }
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
            FileStatusStore.updateStatuses([
              ...loadedFiles.map((f) => [f.id, "loaded"] as [FileId, "loaded"]),
              ...[...erroredFiles.keys()].map(
                (id) => [id, "error"] as [FileId, "error"],
              ),
            ]);
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(async ({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({
            currentFileIds: fileIds,
          });
        }
      }
    },
    [collabAPI, excalidrawAPI],
  );

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              elements: restoreElements(data.scene.elements, null, {
                repairBindings: true,
              }),
              appState: restoreAppState(data.scene.appState, null),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode, loadImages]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // Snapshot the scene the moment we enter presentation (before annotations).
    if (appState.presentationModeEnabled && !prevPresentationRef.current) {
      presentationSnapshotRef.current = elements;
    }
    prevPresentationRef.current = appState.presentationModeEnabled;
    setIsPresentation(appState.presentationModeEnabled);

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Keep the floating notebook page bar in sync with the surface (mode, page
    // count, and which page is currently most in view).
    const nbMode = !!appState.notebookMode;
    const nbCount = Math.max(1, Math.floor(appState.notebookPageCount ?? 1));
    const nbActive = nbMode ? notebookActivePage(appState) : 0;
    const nbStyle = (appState.notebookPageStyle ??
      "ruled") as NotebookPageStyle;
    const nbOpacity = Math.max(
      0,
      Math.min(100, Math.round(appState.notebookPageOpacity ?? 100)),
    );
    if (
      nbMode !== notebookUI.mode ||
      nbCount !== notebookUI.count ||
      nbActive !== notebookUI.active ||
      nbStyle !== notebookUI.style ||
      nbOpacity !== notebookUI.opacity
    ) {
      setNotebookUI({
        mode: nbMode,
        count: nbCount,
        active: nbActive,
        style: nbStyle,
        opacity: nbOpacity,
      });
    }

    // Persist the active canvas. Skipped in presentation mode (ephemeral).
    // Always write to the durable local cache + mark pending (survives reboots
    // and offline), then debounce-upload to the backend; clearing the pending
    // flag only once the upload succeeds. Offline → stays pending and syncs
    // later (see syncNow on reconnect).
    //
    // canvasReadyRef guards against a race: Excalidraw fires onChange with its
    // own init elements (LocalData restore, empty scene) before our
    // useEffect has finished fetching from the server. Without this guard,
    // that spurious onChange would write an empty/stale pending entry into
    // IndexedDB, which the canvas-load effect would then read and apply
    // instead of fetching the real content from the backend.
    if (
      activeCanvasId &&
      !appState.presentationModeEnabled &&
      canvasReadyRef.current
    ) {
      const scene = {
        type: "excalidraw",
        version: 2,
        source: "self-hosted",
        elements: elements as unknown[],
        appState: appState.notebookMode
          ? {
              viewBackgroundColor: appState.viewBackgroundColor,
              notebookMode: true,
              notebookPageStyle: appState.notebookPageStyle,
              notebookPageCount: appState.notebookPageCount,
              notebookPageOpacity: appState.notebookPageOpacity,
            }
          : { viewBackgroundColor: appState.viewBackgroundColor },
        files: files as Record<string, unknown>,
      };
      const canvasIdAtSchedule = activeCanvasId;
      void cacheScene(canvasIdAtSchedule, scene as never);
      void setPending(canvasIdAtSchedule, scene as never);
      if (saveTimerRef.current !== null) {
        window.clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = window.setTimeout(() => {
        if (!navigator.onLine) {
          return; // stays pending; will sync on reconnect via syncNow
        }
        // Conflict-safe save: if another session modified the canvas while we
        // were editing, reconcile before writing. reconcileSceneForSync returns
        // null when there is no conflict (fast path — no extra fetch).
        void (async () => {
          const sceneToSave =
            (await reconcileSceneForSync(
              canvasIdAtSchedule,
              scene as never,
            ).catch(() => null)) ?? scene;
          libraryApi
            .saveScene(canvasIdAtSchedule, sceneToSave)
            .then(({ updated_at }) => {
              // Record the server's updated_at so the remote-refresh check in
              // syncNow knows this version is already applied locally.
              lastCanvasUpdatedAtRef.current = updated_at;
              return clearPending(canvasIdAtSchedule);
            })
            .catch(() => {
              // save failed — pending stays, next syncNow will retry
            });
        })();
      }, 1500);
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  // const onExport = () => {
  //   return new Promise((r) => setTimeout(r, 2500));
  //   // console.log("onExport");
  // };

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        onChange={onChange}
        onExport={onExport}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile) {
            return null;
          }
          if (isPresentation) {
            // Clean presentation view: only an exit affordance.
            return (
              <div className="excalidraw-ui-top-right">
                <button
                  type="button"
                  className="pw-exit-presentation"
                  onClick={requestExitPresentation}
                  style={{
                    background: "#2f54eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: "0.5rem",
                    padding: "0.5rem 0.9rem",
                    fontWeight: 600,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(15,23,42,0.2)",
                  }}
                >
                  {t("presentation.exit")}
                </button>
              </div>
            );
          }
          const collabEnabled = collabAPI && !isCollabDisabled;
          return (
            <div className="excalidraw-ui-top-right">
              {collabEnabled && collabError.message && (
                <CollabError collabError={collabError} />
              )}
              <LiveCollaborationTrigger
                isCollaborating={isCollaborating}
                onSelect={() =>
                  setShareDialogState({ isOpen: true, type: "share" })
                }
                editorInterface={editorInterface}
              />
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
          onLibraryChanged={bumpLibrary}
          onRequestExitPresentation={requestExitPresentation}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          excalidrawAPI={excalidrawAPI}
          activeCanvasId={activeCanvasId}
        />

        {!isPresentation && (
          <CanvasLibrarySidebar
            activeCanvasId={activeCanvasId}
            onSelectCanvas={handleSelectCanvas}
            onCanvasDeleted={handleCanvasDeleted}
            onLibraryChanged={bumpLibrary}
            reloadKey={libraryVersion}
          />
        )}

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: [
                "issues",
                "bugs",
                "requests",
                "report",
                "features",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://github.com/excalidraw/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.followUs"),
              icon: XBrandIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["twitter", "contact", "social", "community"],
              perform: () => {
                window.open(
                  "https://x.com/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.discordChat"),
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              icon: DiscordIcon,
              keywords: [
                "chat",
                "talk",
                "contact",
                "bugs",
                "requests",
                "report",
                "feedback",
                "suggestions",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://discord.gg/UexuTaE",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: "YouTube",
              icon: youtubeIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["features", "tutorials", "howto", "help", "community"],
              perform: () => {
                window.open(
                  "https://youtube.com/@excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
      {activeCanvasId && !isPresentation && (
        <CanvasTitleBar
          activeCanvasId={activeCanvasId}
          libraryVersion={libraryVersion}
          onRenamed={bumpLibrary}
          theme={editorTheme}
        />
      )}
      <StickyNoteToolHandler excalidrawAPI={excalidrawAPI} />
      {activeCanvasId && notebookUI.mode && !isPresentation && (
        <div
          className={clsx("pw-notebook-bar", {
            "pw-notebook-bar--offline": isOffline,
          })}
        >
          <button
            type="button"
            className="pw-notebook-bar__btn"
            onClick={() => notebookGoToPage(notebookUI.active - 1)}
            disabled={notebookUI.active <= 0}
            title={t("notebook.prevPage")}
            aria-label={t("notebook.prevPage")}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className="pw-notebook-bar__label">
            {t("notebook.pageIndicator", {
              current: notebookUI.active + 1,
              total: notebookUI.count,
            })}
          </span>
          <button
            type="button"
            className="pw-notebook-bar__btn"
            onClick={() => notebookGoToPage(notebookUI.active + 1)}
            disabled={notebookUI.active >= notebookUI.count - 1}
            title={t("notebook.nextPage")}
            aria-label={t("notebook.nextPage")}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
          <span className="pw-notebook-bar__sep" />
          <button
            type="button"
            className="pw-notebook-bar__btn"
            onClick={() => setNotebookStyleOpen((v) => !v)}
            title={t("notebook.pageStyleLabel")}
            aria-label={t("notebook.pageStyleLabel")}
            aria-expanded={notebookStyleOpen}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
            </svg>
          </button>
          {notebookStyleOpen && (
            <div className="pw-notebook-styles" role="menu">
              <div className="pw-notebook-styles__row">
                {(
                  [
                    { key: "ruled", label: t("notebook.style.ruled") },
                    { key: "grid", label: t("notebook.style.grid") },
                    { key: "dotted", label: t("notebook.style.dotted") },
                    { key: "blank", label: t("notebook.style.blank") },
                  ] as { key: NotebookPageStyle; label: string }[]
                ).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={notebookUI.style === opt.key}
                    className={clsx("pw-notebook-styles__item", {
                      "pw-notebook-styles__item--active":
                        notebookUI.style === opt.key,
                    })}
                    onClick={() => setNotebookStyle(opt.key)}
                  >
                    <span
                      className={`pw-notebook-styles__swatch pw-notebook-styles__swatch--${opt.key}`}
                    />
                    {opt.label}
                  </button>
                ))}
              </div>
              <div
                className={clsx("pw-notebook-styles__opacity", {
                  "pw-notebook-styles__opacity--disabled":
                    notebookUI.style === "blank",
                })}
              >
                <span className="pw-notebook-styles__opacity-label">
                  {t("grid.opacity")}
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={notebookUI.opacity}
                  disabled={notebookUI.style === "blank"}
                  onChange={(e) => setNotebookOpacity(Number(e.target.value))}
                  aria-label={t("grid.opacity")}
                />
                <span className="pw-notebook-styles__opacity-value">
                  {notebookUI.opacity}%
                </span>
              </div>
            </div>
          )}
          <span className="pw-notebook-bar__sep" />
          <button
            type="button"
            className="pw-notebook-bar__btn pw-notebook-bar__btn--danger"
            onClick={deleteNotebookPage}
            disabled={notebookUI.count <= 1}
            title={t("notebook.deletePage")}
            aria-label={t("notebook.deletePage")}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6" />
              <path d="M10 11v6M14 11v6" />
            </svg>
          </button>
          <button
            type="button"
            className="pw-notebook-bar__btn pw-notebook-bar__btn--primary"
            onClick={addNotebookPage}
            title={t("notebook.addPage")}
            aria-label={t("notebook.addPage")}
          >
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span className="pw-notebook-bar__add-label">
              {t("notebook.addPage")}
            </span>
          </button>
        </div>
      )}
      {isOffline && (
        <div className="pw-offline-badge">{t("offline.badge")}</div>
      )}
      <UserManagementDialog />
      <GridSettingsDialog excalidrawAPI={excalidrawAPI} />
      {exitDialogOpen && (
        <PresentationExitDialog
          onSavePdf={handleExitSavePdf}
          onSaveAsNewBoard={handleExitSaveAsNewBoard}
          onDiscard={finishExitPresentation}
          onCancel={() => setExitDialogOpen(false)}
        />
      )}
    </div>
  );
};

const ExcalidrawApp = () => {
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  const shareMatch = window.location.pathname.match(/^\/share\/([\w-]+)$/);
  if (shareMatch) {
    return <SharePublicViewer token={shareMatch[1]} />;
  }

  return (
    <TopErrorBoundary>
      <AuthGate>
        <Provider store={appJotaiStore}>
          <ExcalidrawAPIProvider>
            <ExcalidrawWrapper />
          </ExcalidrawAPIProvider>
        </Provider>
      </AuthGate>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
